import { API_BASE_URL } from '../config';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const calculateBackoff = (attempt, baseDelay = 1000) => {
  return baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
};

const API_URL = API_BASE_URL.endsWith('/') 
  ? API_BASE_URL.slice(0, -1) 
  : API_BASE_URL;

const validateImageFile = (file) => {
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'image/gif'];
  
  if (!file || !file.type) {
    throw new Error('No se seleccionó ningún archivo válido');
  }
  
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Formato no permitido. Use JPEG, PNG, WebP o GIF');
  }
  
  if (file.size > maxSize) {
    throw new Error('La imagen es demasiado grande. Máximo 5MB');
  }
  
  return {
    valid: true,
    type: file.type,
    size: file.size
  };
};

const uploadToServer = async (file, config, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const xhr = new XMLHttpRequest();
  
  return new Promise((resolve, reject) => {
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress?.(progress);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const response = JSON.parse(xhr.responseText);
          
          if (response.error === 0) {
            resolve({
              success: true,
              url: response.data.url,
              fileName: response.data.fileName
            });
          } else {
            reject(new Error(response.mensaje || 'Error en el servidor'));
          }
        } catch (error) {
          reject(new Error('Respuesta inválida del servidor'));
        }
      } else {
        reject(new Error(`Error en subida: ${xhr.status} ${xhr.statusText}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('Error de conexión al subir imagen'));
    });
    
    xhr.addEventListener('timeout', () => {
      reject(new Error('Tiempo de espera agotado'));
    });
    
    xhr.open('POST', `${config.baseURL}/upload`);
    xhr.send(formData);
  });
};

const uploadImageWithRetry = async (file, options = {}) => {
  const config = {
    baseURL: API_URL,
    timeout: 30000
  };
  
  const maxRetries = options.maxRetries || 3;
  const onProgress = options.onProgress;
  
  validateImageFile(file);
  
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        const backoffDelay = calculateBackoff(attempt - 1);
        await sleep(backoffDelay);
        onProgress?.(0);
      }
      
      const result = await uploadToServer(file, config, onProgress);
      
      return {
        ...result,
        attempts: attempt + 1
      };
      
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        break;
      }
      
      console.warn(`Intento ${attempt + 1} fallido, reintentando...`, error.message);
    }
  }
  
  throw new Error(`Error al subir imagen después de ${maxRetries + 1} intentos: ${lastError.message}`);
};

const generateImageHash = async (file) => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const checkDuplicateImage = async (file) => {
  try {
    const hash = await generateImageHash(file);
    const cachedHashes = JSON.parse(localStorage.getItem('uploadedImages') || '{}');
    
    if (cachedHashes[hash]) {
      return {
        isDuplicate: true,
        existingUrl: cachedHashes[hash]
      };
    }
    
    return { isDuplicate: false };
  } catch (error) {
    console.warn('No se pudo verificar duplicados:', error);
    return { isDuplicate: false };
  }
};

const cacheUploadedImage = (file, url) => {
  generateImageHash(file).then(hash => {
    try {
      const cachedHashes = JSON.parse(localStorage.getItem('uploadedImages') || '{}');
      cachedHashes[hash] = url;
      localStorage.setItem('uploadedImages', JSON.stringify(cachedHashes));
    } catch (error) {
      console.warn('No se pudo cachear la imagen:', error);
    }
  });
};

const deleteImageFromServer = async (fileName) => {
  try {
    const response = await fetch(`${API_URL}/upload`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fileName })
    });
    
    const data = await response.json();
    
    if (data.error === 0) {
      return { success: true, mensaje: data.mensaje };
    } else {
      throw new Error(data.mensaje || 'Error al eliminar archivo');
    }
  } catch (error) {
    throw new Error(`Error de conexión: ${error.message}`);
  }
};

const imageService = {
  upload: async (file, options = {}) => {
    validateImageFile(file);
    
    const duplicateCheck = await checkDuplicateImage(file);
    
    if (duplicateCheck.isDuplicate && options.skipDuplicate !== true) {
      return {
        success: true,
        url: duplicateCheck.existingUrl,
        isDuplicate: true,
        fileName: file.name
      };
    }
    
    const result = await uploadImageWithRetry(file, options);
    
    if (result.success) {
      cacheUploadedImage(file, result.url);
    }
    
    return result;
  },
  
  deleteImage: async (fileName) => {
    if (!fileName) {
      throw new Error('Nombre de archivo requerido');
    }
    return deleteImageFromServer(fileName);
  },
  
  validateFile: validateImageFile,
  
  getUploadProgress: (file) => {
    return new Promise((resolve, reject) => {
      const result = uploadImageWithRetry(file, {
        onProgress: resolve,
        maxRetries: 0
      });
      
      result.catch(reject);
    });
  }
};

export default imageService;
